import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import axios from "axios";
import { toast } from "sonner";
import {
  User, Mail, Lock, Eye, EyeOff, Moon, Sun, Save,
  Zap, BookOpen, Brain, Flame, Shield, ArrowLeft
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";

export default function Settings({ api }) {
  const navigate = useNavigate();
  const { user, refreshAuth } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();

  // Profile form
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [profileLoading, setProfileLoading] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Stats
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${api}/stats`, { withCredentials: true });
        setStats(res.data);
      } catch {
        // ignore
      }
    };
    fetchStats();
  }, [api]);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name cannot be empty");
      return;
    }
    setProfileLoading(true);
    try {
      await axios.put(`${api}/auth/profile`, { name: name.trim(), email: email.trim() }, { withCredentials: true });
      await refreshAuth();
      toast.success("Profile updated!");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to update profile");
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.put(`${api}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      }, { withCredentials: true });
      toast.success("Password changed!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDarkModeToggle = async () => {
    toggleDarkMode();
    try {
      await axios.put(`${api}/auth/preferences`, { dark_mode: !darkMode }, { withCredentials: true });
    } catch {
      // Local toggle still works
    }
  };

  const isGoogleAuth = user?.auth_type === "google";

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/")} className="neo-btn bg-white dark:bg-[#1E1E1E] dark:border-[#444] dark:text-white p-2" data-testid="settings-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight dark:text-white">Settings</h1>
          <p className="text-neo-text-secondary dark:text-gray-400 mt-1">Manage your account and preferences</p>
        </div>
      </div>

      {/* Account Overview */}
      {stats && (
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
          <h2 className="font-heading font-bold text-lg mb-4 dark:text-white flex items-center gap-2">
            <Zap className="w-5 h-5 text-neo-yellow" />
            Account Overview
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-neo-yellow border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center mx-auto mb-2">
                <Zap className="w-5 h-5 text-neo-text" />
              </div>
              <p className="font-heading font-extrabold text-xl dark:text-white" data-testid="settings-xp">{stats.total_xp}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Total XP</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-neo-green border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center mx-auto mb-2">
                <BookOpen className="w-5 h-5 text-neo-text" />
              </div>
              <p className="font-heading font-extrabold text-xl dark:text-white">{stats.papers_completed}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Papers</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-neo-blue border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center mx-auto mb-2">
                <Brain className="w-5 h-5 text-neo-text" />
              </div>
              <p className="font-heading font-extrabold text-xl dark:text-white">{stats.total_pages_read}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Pages</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 rounded-lg bg-neo-red border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center mx-auto mb-2">
                <Flame className="w-5 h-5 text-white" />
              </div>
              <p className="font-heading font-extrabold text-xl dark:text-white">{stats.current_streak}</p>
              <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Streak</p>
            </div>
          </div>
        </div>
      )}

      {/* Appearance */}
      <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
        <h2 className="font-heading font-bold text-lg mb-4 dark:text-white flex items-center gap-2">
          {darkMode ? <Moon className="w-5 h-5 text-neo-purple" /> : <Sun className="w-5 h-5 text-neo-yellow" />}
          Appearance
        </h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-heading font-bold dark:text-white">Dark Mode</p>
            <p className="text-sm text-neo-text-secondary dark:text-gray-400">Switch between light and dark themes</p>
          </div>
          <Switch
            checked={darkMode}
            onCheckedChange={handleDarkModeToggle}
            data-testid="dark-mode-toggle"
          />
        </div>
      </div>

      {/* Profile */}
      <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
        <h2 className="font-heading font-bold text-lg mb-4 dark:text-white flex items-center gap-2">
          <User className="w-5 h-5 text-neo-blue" />
          Profile
        </h2>
        {isGoogleAuth && (
          <div className="mb-4 p-3 rounded-lg border-2 border-neo-blue bg-neo-blue/10 text-sm dark:text-white flex items-center gap-2">
            <Shield className="w-4 h-4 flex-shrink-0" />
            Connected via Google Account
          </div>
        )}
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Name</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="pl-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                data-testid="settings-name-input"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                disabled={isGoogleAuth}
                className="pl-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow disabled:opacity-50"
                data-testid="settings-email-input"
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={profileLoading}
            className={`neo-btn flex items-center gap-2 ${profileLoading ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed" : "neo-btn-primary"}`}
            data-testid="save-profile-btn"
          >
            <Save className="w-4 h-4" />
            {profileLoading ? "Saving..." : "Save Profile"}
          </button>
        </form>
      </div>

      {/* Change Password - Only for email auth users */}
      {!isGoogleAuth && (
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]">
          <h2 className="font-heading font-bold text-lg mb-4 dark:text-white flex items-center gap-2">
            <Lock className="w-5 h-5 text-neo-red" />
            Change Password
          </h2>
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Current Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                  className="pl-10 pr-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPasswords(!showPasswords)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neo-text-secondary dark:text-gray-400"
                >
                  {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                  className="pl-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                  data-testid="new-password-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-1">Confirm New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neo-text-secondary dark:text-gray-400" />
                <Input
                  type={showPasswords ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  className="pl-10 border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                  data-testid="confirm-new-password-input"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={passwordLoading}
              className={`neo-btn flex items-center gap-2 ${passwordLoading ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed" : "neo-btn-danger"}`}
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4" />
              {passwordLoading ? "Changing..." : "Change Password"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
